// Transaction drafts — Supabase implementation.
//
// Isolation is enforced by RLS on cashier_id = auth.uid(), so these queries
// never filter by cashier themselves: another cashier's drafts are simply not
// visible, and that holds for owners too.

import { supabase } from '@/lib/supabase';
import { tsReq } from './mappers';
import type { Database } from '@/types/database';
import type { TransactionDraft } from '@/types/domain';

type Row = Database['public']['Tables']['transaction_drafts']['Row'];

function mapDraft(r: Row): TransactionDraft {
  return {
    id: r.id,
    cashierId: r.cashier_id,
    cashierName: r.cashier_name,
    eventId: r.event_id ?? '',
    eventName: r.event_name ?? '',
    eventCode: r.event_code ?? '',
    label: r.label,
    items: (r.items as unknown as TransactionDraft['items']) ?? [],
    subtotal: r.subtotal,
    discountType: r.discount_type,
    discountValue: r.discount_value,
    discountAmount: r.discount_amount,
    taxEnabled: r.tax_enabled,
    taxRate: Number(r.tax_rate),
    taxAmount: r.tax_amount,
    grandTotal: r.grand_total,
    fulfillment: r.fulfillment,
    customerId: r.customer_id,
    customerSnapshot: r.customer_snapshot as TransactionDraft['customerSnapshot'],
    notes: r.notes,
    createdAt: tsReq(r.created_at),
    updatedAt: tsReq(r.updated_at),
  };
}

/**
 * `cashierId` is accepted for call-site compatibility and ignored: RLS scopes
 * this to auth.uid() already, so passing someone else's id would return
 * nothing rather than their drafts.
 */
export async function listMyDrafts(_cashierId?: string): Promise<TransactionDraft[]> {
  const { data, error } = await supabase
    .from('transaction_drafts')
    .select('*')
    .order('updated_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapDraft);
}

export async function getDraft(id: string): Promise<TransactionDraft | null> {
  const { data, error } = await supabase
    .from('transaction_drafts').select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapDraft(data) : null;
}

export interface SaveDraftInput {
  id?: string;
  /** Ignored — the owning cashier is taken from the session. */
  cashierId?: string;
  cashierName?: string;
  eventId: string;
  eventName: string;
  eventCode: string;
  label: string;
  items: TransactionDraft['items'];
  subtotal: number;
  discountType: TransactionDraft['discountType'];
  discountValue: number;
  discountAmount: number;
  taxEnabled: boolean;
  taxRate: number;
  taxAmount: number;
  grandTotal: number;
  fulfillment: TransactionDraft['fulfillment'];
  customerId?: string | null;
  customerSnapshot?: TransactionDraft['customerSnapshot'];
  notes?: string | null;
}

export async function saveDraft(input: SaveDraftInput): Promise<TransactionDraft> {
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth.user?.id;
  if (!uid) throw new Error('Not signed in');

  const { data: profile } = await supabase
    .from('profiles').select('display_name').eq('id', uid).maybeSingle();

  const row = {
    // Set explicitly because the RLS check is cashier_id = auth.uid(); there
    // is no default that would fill it in.
    cashier_id: uid,
    cashier_name: profile?.display_name ?? '',
    event_id: input.eventId,
    event_name: input.eventName,
    event_code: input.eventCode,
    label: input.label,
    items: input.items as never,
    subtotal: input.subtotal,
    discount_type: input.discountType,
    discount_value: input.discountValue,
    discount_amount: input.discountAmount,
    tax_enabled: input.taxEnabled,
    tax_rate: input.taxRate,
    tax_amount: input.taxAmount,
    grand_total: input.grandTotal,
    fulfillment: input.fulfillment,
    customer_id: input.customerId ?? null,
    customer_snapshot: (input.customerSnapshot ?? null) as never,
    notes: input.notes ?? null,
  };

  const q = input.id
    ? supabase.from('transaction_drafts').update(row).eq('id', input.id)
    : supabase.from('transaction_drafts').insert(row);
  const { data, error } = await q.select('*').single();
  if (error) throw new Error(error.message);
  return mapDraft(data);
}

export async function renameDraft(id: string, label: string): Promise<void> {
  const { error } = await supabase
    .from('transaction_drafts').update({ label }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteDraft(id: string): Promise<void> {
  const { error } = await supabase.from('transaction_drafts').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
